import React from 'react';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  BarChart3, Star, Users, BookOpen, TrendingUp, CheckCircle
} from 'lucide-react';

function MetricCard({ label, value, subtext, icon: Icon, color }) {
  return (
    <Card className="border-slate-100">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
            {subtext && <p className="mt-0.5 text-xs text-slate-400">{subtext}</p>}
          </div>
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AuthorAnalyticsTab() {
  const { readers, reviews, campaigns, books } = useAuthorDashboard();

  // ARCs sent vs reviews received
  const totalArcsSent = readers.filter(r => r.status === 'approved').length;
  const totalReviews = reviews.filter(r => r.status === 'approved').length;
  const completionRate = totalArcsSent > 0
    ? Math.round((totalReviews / totalArcsSent) * 100) : 0;

  // Average rating
  const ratingsOnly = reviews.filter(r => r.rating && r.status === 'approved');
  const avgRating = ratingsOnly.length > 0
    ? (ratingsOnly.reduce((s, r) => s + Number(r.rating), 0) / ratingsOnly.length).toFixed(1)
    : 'N/A';

  // Platform breakdown
  const platformCounts = {};
  reviews.forEach(r => {
    if (r.platforms) {
      r.platforms.forEach(p => {
        platformCounts[p] = (platformCounts[p] || 0) + 1;
      });
    }
    // Also count individual link fields
    ['amazon', 'goodreads', 'instagram', 'tiktok', 'blog', 'bookbub', 'storygraph'].forEach(p => {
      if (r[p] || r[`${p}Link`]) {
        platformCounts[p] = (platformCounts[p] || 0) + 1;
      }
    });
  });

  // Genre breakdown of applicants
  const genreCounts = {};
  readers.forEach(r => {
    (r.genres || []).forEach(g => {
      const label = g.label || g;
      genreCounts[label] = (genreCounts[label] || 0) + 1;
    });
  });

  // Reader retention (readers in multiple campaigns)
  const readerCampaignCount = {};
  readers.forEach(r => {
    const count = r.campaignIds?.length || 1;
    if (count > 1) readerCampaignCount[r.id] = count;
  });
  const returningReaders = Object.keys(readerCampaignCount).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        <p className="text-sm text-slate-500">Aggregated performance across all your campaigns</p>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="ARCs Sent" value={totalArcsSent}
          icon={BookOpen} color="bg-blue-100 text-blue-600"
        />
        <MetricCard
          label="Reviews Received" value={totalReviews}
          subtext={`${completionRate}% completion`}
          icon={CheckCircle} color="bg-green-100 text-green-600"
        />
        <MetricCard
          label="Average Rating" value={avgRating}
          subtext={`${ratingsOnly.length} rated reviews`}
          icon={Star} color="bg-yellow-100 text-yellow-600"
        />
        <MetricCard
          label="Returning Readers" value={returningReaders}
          subtext="Joined 2+ campaigns"
          icon={Users} color="bg-purple-100 text-purple-600"
        />
      </div>

      {/* Completion Rate Bar */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Review Completion Rate</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-3 rounded-full bg-slate-100">
                <div
                  className="h-3 rounded-full bg-teal-500 transition-all"
                  style={{ width: `${Math.min(completionRate, 100)}%` }}
                />
              </div>
            </div>
            <span className="text-lg font-bold text-slate-900">{completionRate}%</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {totalReviews} of {totalArcsSent} ARC recipients submitted a review
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Review Platform Breakdown */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Review Platforms</h3>
            {Object.keys(platformCounts).length === 0 ? (
              <p className="text-sm text-slate-400">No review data yet</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(platformCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([platform, count]) => (
                    <div key={platform} className="flex items-center justify-between">
                      <span className="text-sm capitalize text-slate-700">{platform}</span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 rounded-full bg-slate-100">
                          <div
                            className="h-2 rounded-full bg-teal-400"
                            style={{
                              width: `${Math.round((count / Math.max(...Object.values(platformCounts))) * 100)}%`
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Genre Interest */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Applicant Genre Interests</h3>
            {Object.keys(genreCounts).length === 0 ? (
              <p className="text-sm text-slate-400">No genre data yet</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(genreCounts)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 12)
                  .map(([genre, count]) => (
                    <Badge key={genre} className="border-0 bg-slate-100 text-slate-700">
                      {genre} ({count})
                    </Badge>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
